import { PlayIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useWorkspace } from '@/features/workspaces';
import { useCanWork } from '@/features/workspaces/canWork';
import type { TaskView } from '@/lib/api/generated/model';

import { useTaskCommands } from '../hooks/useTaskCommands';
import {
  useStartLinkedTasks,
  useStartingLinkedTasks,
} from '../hooks/useStartLinkedTasks';
import { derivePrimaryAction, type TaskChoice } from '../primaryAction';
import { taskPlacement } from '../taskPlacement';

/**
 * **Start all ({n})** in the head of a parent's created-task list (spec 140
 * US2): one request starts every task the join is waiting on. "Starting…"
 * while it runs, wherever it was asked for — here or from the palette.
 */
function StartAllChildrenControl({
  workspaceId,
  task,
  count,
  density = 'header',
  emphasis = 'quiet',
  onChoose,
}: {
  workspaceId: string;
  task: Pick<TaskView, 'id' | 'title'>;
  count: number;
  /**
   * `compact` is a row in Your turn (spec 152 FR-007): "Start 2", named for
   * its task, because several parents can offer one side by side. `header`
   * is the drawer's "Start all (2)".
   */
  density?: 'header' | 'compact';
  /** Primary only on the row the person is on (spec 152 FR-008). */
  emphasis?: 'primary' | 'quiet';
  /** Report the choice instead of starting (spec 154 R2): Your turn holds it. */
  onChoose?: (choice: TaskChoice) => void;
}) {
  const { startAll } = useStartLinkedTasks(workspaceId);
  const starting = useStartingLinkedTasks(task.id);
  const compact = density === 'compact';
  const shown = starting
    ? 'Starting…'
    : compact
      ? `Start ${String(count)}`
      : `Start all (${String(count)})`;
  return (
    <Button
      type="button"
      size={compact ? 'xs' : 'sm'}
      variant={emphasis === 'primary' ? 'default' : 'outline'}
      disabled={starting}
      aria-label={
        compact
          ? starting
            ? `Starting the subtasks of ${task.title}`
            : `Start ${String(count)} subtask${count === 1 ? '' : 's'} of ${task.title}`
          : undefined
      }
      onClick={() => {
        if (onChoose) onChoose({ kind: 'start-subtasks' });
        else startAll(task);
      }}
    >
      <PlayIcon aria-hidden="true" strokeWidth={1.5} />
      {shown}
    </Button>
  );
}

/**
 * **Start** on one waiting created task (spec 140 US2): the same start its own
 * card would make, at the step its own board would start it at. The child
 * lives on another workflow's board, so it is placed against that board here.
 */
function StartChildControl({
  workspaceId,
  childId,
  childTitle,
}: {
  workspaceId: string;
  childId: string;
  childTitle: string;
}) {
  const workspace = useWorkspace(workspaceId);
  const commands = useTaskCommands(workspaceId);
  const placement = workspace.data
    ? taskPlacement(workspace.data.workflows, workspace.data.tasks, childId)
    : undefined;
  const child = placement?.task;
  const action =
    child && placement
      ? derivePrimaryAction(child, placement.location?.step, placement.steps)
      : undefined;
  if (!child || action?.kind !== 'start') return null;
  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      aria-label={`Start ${childTitle}`}
      onClick={() => {
        commands.start(child, action.target);
      }}
    >
      Start
    </Button>
  );
}

/**
 * Offered only to someone who may work in the workspace (spec 189): a person reading it gets nothing that would only be refused.
 */
export function StartAllChildren(
  props: Parameters<typeof StartAllChildrenControl>[0],
) {
  return useCanWork() ? <StartAllChildrenControl {...props} /> : null;
}

/** Offered only to someone who may work in the workspace (spec 189). */
export function StartChild(props: Parameters<typeof StartChildControl>[0]) {
  return useCanWork() ? <StartChildControl {...props} /> : null;
}
